import { collection, doc, setDoc, deleteDoc, query, onSnapshot, serverTimestamp, Unsubscribe, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { ReviewItem } from '../types';
import { perfTracker } from './perfLogger';

const REVIEWS_COLLECTION = 'reviews';
const CACHE_KEY = 'rh_cached_reviews';

export function getLocalCachedReviews(): ReviewItem[] {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (e) {}
  return [];
}

const reviewSubscribers = new Set<(reviews: ReviewItem[]) => void>();
let activeReviewsUnsub: Unsubscribe | null = null;

export async function fetchReviewsFromFirestore(): Promise<ReviewItem[]> {
  const startTime = performance.now();
  try {
    const q = query(collection(db, REVIEWS_COLLECTION));
    const snapshot = await getDocs(q);
    const duration = performance.now() - startTime;
    perfTracker.logFetch(REVIEWS_COLLECTION, duration, snapshot.metadata.fromCache ? 'cache' : 'server', snapshot.size);

    const fetched: ReviewItem[] = [];
    snapshot.forEach((docSnap) => {
      fetched.push({ id: docSnap.id, ...docSnap.data() } as ReviewItem);
    });
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(fetched));
    } catch (e) {}
    return fetched;
  } catch (err) {
    console.error('Fetch reviews error:', err);
    return getLocalCachedReviews();
  }
}

export function subscribeToReviewsInFirestore(onReviewsUpdate: (reviews: ReviewItem[]) => void): () => void {
  onReviewsUpdate(getLocalCachedReviews());

  if (reviewSubscribers.has(onReviewsUpdate)) {
    perfTracker.logDuplicatePrevented(REVIEWS_COLLECTION);
    return () => reviewSubscribers.delete(onReviewsUpdate);
  }

  reviewSubscribers.add(onReviewsUpdate);

  if (!activeReviewsUnsub) {
    perfTracker.registerListenerStart(REVIEWS_COLLECTION);
    const q = query(collection(db, REVIEWS_COLLECTION));

    activeReviewsUnsub = onSnapshot(
      q,
      (snapshot) => {
        const startTime = performance.now();
        const fetched: ReviewItem[] = [];
        snapshot.forEach((docSnap) => {
          fetched.push({ id: docSnap.id, ...docSnap.data() } as ReviewItem);
        });

        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(fetched));
        } catch (e) {}

        const duration = performance.now() - startTime;
        perfTracker.logFetch(REVIEWS_COLLECTION, duration, snapshot.metadata.fromCache ? 'cache' : 'snapshot', fetched.length);

        reviewSubscribers.forEach((cb) => {
          try { cb(fetched); } catch (e) {}
        });
      },
      (err) => {
        console.error('Reviews snapshot error:', err);
      }
    );
  } else {
    perfTracker.logDuplicatePrevented(REVIEWS_COLLECTION);
  }

  return () => {
    reviewSubscribers.delete(onReviewsUpdate);
    if (reviewSubscribers.size === 0 && activeReviewsUnsub) {
      activeReviewsUnsub();
      activeReviewsUnsub = null;
      perfTracker.registerListenerStop(REVIEWS_COLLECTION);
    }
  };
}

export async function saveReviewToFirestore(review: ReviewItem): Promise<void> {
  try {
    await setDoc(doc(db, REVIEWS_COLLECTION, review.id), {
      ...review,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (err) {
    console.error('Error saving review to Firestore:', err);
  }
}

export async function deleteReviewFromFirestore(reviewId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, REVIEWS_COLLECTION, reviewId));
  } catch (err) {
    console.error('Error deleting review from Firestore:', err);
  }
}
